import tinycolor from "tinycolor2";

export type Theme = "light" | "dark" | "custom" | null;

export const cssVars = [
  "font-color",
  "link-color",
  "background-color",
  "border-color",
  "button-color",
  "good-lift",
  "bad-lift",
  "current-lift",
] as const;

export type CssVars = Record<(typeof cssVars)[number], string>;

export type ThemeSettingsValue = {
  theme: Theme;
  customTheme: CssVars;
};

export const defaultDarkVars: CssVars = {
  "font-color": "#f0f0f0",
  "link-color": "#ffffff",
  "background-color": "#000000",
  "border-color": "#a9a9a9",
  "button-color": "#444444",
  "good-lift": "#006600",
  "bad-lift": "#cc0000",
  "current-lift": "#333366",
};

export const defaultLightVars: CssVars = {
  "font-color": "#101010",
  "link-color": "#000000",
  "background-color": "#ffffff",
  "border-color": "#444444",
  "button-color": "#aaaaaa",
  "good-lift": "#008800",
  "bad-lift": "#ff1a1a",
  "current-lift": "#7575a6",
};

export const themeOptions = [
  { label: "Dark Mode", value: "dark" },
  { label: "Light Mode", value: "light" },
  { label: "Custom Theme", value: "custom" },
];

const isReadable = (
  color1: string | undefined | null,
  color2: string | undefined | null,
) => {
  if (!color1 || !color2) {
    return false;
  }
  return tinycolor.isReadable(color1, color2, { level: "AA", size: "large" });
};

const secondaryContrastCheck = (
  color1: string | undefined | null,
  color2: string | undefined | null,
) => {
  if (!color1 || !color2) {
    return false;
  }
  return tinycolor.readability(color1, color2) > 1.2;
};

const isValidHexColor = (hexCode: string | undefined | null) => {
  if (!hexCode) {
    return false;
  }
  const hexPattern = /^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/;

  return hexPattern.test(hexCode);
};

export const bulkEditStringify = (vars: CssVars) => {
  return cssVars.map((cssVar) => `${cssVar}:${vars[cssVar]}`).join("\n");
};

export const bulkEditParse = (text: string) => {
  const newCssVars: Partial<CssVars> = {};
  const lines = text.split("\n");
  lines.forEach((line) => {
    const parts = line.split(":");
    const key = parts.at(0)?.trim() as (typeof cssVars)[number];
    const value = parts.at(1)?.trim();
    if (key && value && cssVars.includes(key)) {
      newCssVars[key] = value;
    }
  });
  return newCssVars;
};

export const validateVars = (vars: Partial<CssVars>) => {
  const errors: string[] = [];

  cssVars.forEach((key) => {
    if (!vars[key]) {
      errors.push(`${key} is missing.`);
    }
  });

  cssVars.forEach((key) => {
    if (vars[key] && !isValidHexColor(vars[key])) {
      errors.push(`${key} is not a valid hex value.`);
    }
  });

  if (errors.length) {
    return errors.join(" ");
  }

  if (!isReadable(vars["font-color"], vars["background-color"])) {
    errors.push(
      "Insufficient contrast between font-color and background-color.",
    );
  }
  if (!isReadable(vars["font-color"], vars["button-color"])) {
    errors.push("Insufficient contrast between font-color and button-color.");
  }
  if (!isReadable(vars["link-color"], vars["background-color"])) {
    errors.push(
      "Insufficient contrast between link-color and background-color.",
    );
  }

  if (!isReadable(vars["font-color"], vars["good-lift"])) {
    errors.push("Insufficient contrast between font-color and good-lift.");
  }
  if (!isReadable(vars["font-color"], vars["bad-lift"])) {
    errors.push("Insufficient contrast between font-color and bad-lift.");
  }
  if (!isReadable(vars["font-color"], vars["current-lift"])) {
    errors.push("Insufficient contrast between font-color and current-lift.");
  }

  if (!secondaryContrastCheck(vars["background-color"], vars["good-lift"])) {
    errors.push(
      "Insufficient contrast between background-color and good-lift.",
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], vars["bad-lift"])) {
    errors.push("Insufficient contrast between background-color and bad-lift.");
  }
  if (!secondaryContrastCheck(vars["background-color"], vars["current-lift"])) {
    errors.push(
      "Insufficient contrast between background-color and current-lift.",
    );
  }

  if (!secondaryContrastCheck(vars["background-color"], "#cc0000")) {
    errors.push(
      "Insufficient contrast between background-color and system red.",
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#006600")) {
    errors.push(
      "Insufficient contrast between background-color and system green.",
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#cccc00")) {
    errors.push(
      "Insufficient contrast between background-color and system yellow.",
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#0000cc")) {
    errors.push(
      "Insufficient contrast between background-color and system blue.",
    );
  }

  return errors.join(" ");
};
